import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Phone, MapPin, CalendarClock, History } from 'lucide-react';
import { AgentLayout } from '../../components/AgentLayout';
import { AddMerchantSheet } from '../../components/AddMerchantSheet';
import api from '../../api';

const STATUS_OPTIONS = { New: ['Contacted', 'Onboarded'], Contacted: ['Onboarded'], Onboarded: [] };

function isOverdue(d) { return d && new Date(d) < new Date(new Date().toDateString()); }
function formatDate(d) {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); }
  catch { return d; }
}
function openMaps(lat, lng) {
  if (lat && lng) window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
}

function Toast({ msg, show }) {
  return <div className={`au-toast ${show ? 'show' : ''}`}>{msg}</div>;
}

export default function AgentMerchants() {
  const [searchParams] = useSearchParams();
  const [merchants, setMerchants] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showSheet, setShowSheet] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [historyCard, setHistoryCard] = useState(null);
  const [rescheduleCard, setRescheduleCard] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleNote, setRescheduleNote] = useState('');
  const [toast, setToast] = useState({ show: false, msg: '' });
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2500);
  };

  const loadMerchants = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      const serviceFilter = searchParams.get('service');
      if (serviceFilter) params.append('service', serviceFilter);
      const res = await api.get(`/merchants?${params}`);
      setMerchants(res.data.merchants);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page, searchParams]);

  useEffect(() => { loadMerchants(); }, [loadMerchants]);

  const handleStatusChange = async (merchantId, newStatus) => {
    try {
      await api.patch(`/merchants/${merchantId}/status`, { status: newStatus });
      setMerchants(prev => prev.map(m => m.id === merchantId ? { ...m, status: newStatus } : m));
      showToast(`Status updated to ${newStatus}`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleMerchantAdded = async () => {
    showToast('Merchant registered!');
    await loadMerchants();
  };

  const handleEditSuccess = async () => {
    showToast('Merchant updated!');
    setEditingMerchant(null);
    setShowSheet(false);
    await loadMerchants();
  };

  const handleReschedule = async (merchantId) => {
    if (!rescheduleDate) return;
    try {
      await api.post(`/merchants/${merchantId}/visit-log`, {
        note: rescheduleNote || `Follow-up rescheduled to ${rescheduleDate}`,
        rescheduleDate
      });
      showToast('Follow-up rescheduled');
      setRescheduleCard(null);
      setRescheduleDate('');
      setRescheduleNote('');
      await loadMerchants();
    } catch {
      showToast('Failed to reschedule');
    }
  };

  const openAddSheet = () => {
    setEditingMerchant(null);
    setShowSheet(true);
  };

  const openEditSheet = (merchant) => {
    setEditingMerchant(merchant);
    setShowSheet(true);
  };

  return (
    <AgentLayout onAddClick={openAddSheet}>
      <div className="au-page">
        <div style={{ paddingTop: '14px' }}>
          {/* Search */}
          <div className="search-wrap fu">
            <Search size={14} color="var(--g3)" />
            <input
              className="search-input"
              placeholder="Search by name, phone…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              data-testid="merchant-search"
            />
          </div>

          {/* Filter Tabs */}
          <div className="filter-row fu">
            <button className={`fbtn ${statusFilter === '' ? 'on' : ''}`} onClick={() => { setStatusFilter(''); setPage(1); }} data-testid="filter-all">All</button>
            <button className={`fbtn ${statusFilter === 'New' ? 'on' : ''}`} onClick={() => { setStatusFilter('New'); setPage(1); }} data-testid="filter-new">New</button>
            <button className={`fbtn ${statusFilter === 'Contacted' ? 'on' : ''}`} onClick={() => { setStatusFilter('Contacted'); setPage(1); }} data-testid="filter-contacted">Contacted</button>
            <button className={`fbtn ${statusFilter === 'Onboarded' ? 'on' : ''}`} onClick={() => { setStatusFilter('Onboarded'); setPage(1); }} data-testid="filter-onboarded">Onboarded</button>
          </div>

          <div className="sec-hd fu">
            <span className="sec-t">{total} Merchant{total !== 1 ? 's' : ''}</span>
          </div>

          {/* Merchant List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '32px', color: 'var(--g3)' }}>Loading…</div>
          ) : merchants.length === 0 ? (
            <div className="empty-state fu">
              <div className="empty-icon">🏪</div>
              <div className="empty-title">No merchants found</div>
              <div className="empty-text">Try adjusting your search or filters</div>
            </div>
          ) : (
            merchants.map((m, idx) => {
              const nextOptions = STATUS_OPTIONS[m.status] || [];
              const hasGPS = m.gps_lat && m.gps_lng;
              const showHistory = historyCard === m.id;
              const showReschedule = rescheduleCard === m.id;

              return (
                <div key={m.id} className={`mcard fu${idx % 5}`} data-testid={`merchant-card-${m.id}`}>
                  <div className="mc-body">
                    <div className="mc-top">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="mc-nm">{m.businessName}</div>
                        <div className="mc-ow">{m.ownerName}</div>
                      </div>
                      <span className={`badge-${(m.status || 'new').toLowerCase()}`}>{m.status || 'New'}</span>
                    </div>
                    <div className="mc-meta">
                      <a href={`tel:${m.phone}`} className="mc-mi clickable" onClick={e => e.stopPropagation()}>
                        <Phone size={12} /> {m.phone}
                      </a>
                      {hasGPS && (
                        <span 
                          className="gps-ok clickable" 
                          onClick={() => openMaps(m.gps_lat, m.gps_lng)}
                        >
                          <MapPin size={12} /> GPS
                        </span>
                      )}
                    </div>
                    {(m.services || []).length > 0 && (
                      <div className="mc-tags">
                        {m.services.slice(0, 3).map(s => (
                          <span key={s.id} className={`stag ${s.slug === 'upi_qr' ? 'upi' : ''}`}>{s.name}</span>
                        ))}
                        {m.services.length > 3 && <span className="stag">+{m.services.length - 3}</span>}
                      </div>
                    )}
                    {m.status === 'Onboarded' && m.onboardedAt && (
                      <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '5px', fontWeight: 600 }}>
                        Onboarded: {formatDate(m.onboardedAt)}
                      </div>
                    )}
                  </div>
                  {m.nextVisitDate && (
                    <div className="mc-foot">
                      <span className="mc-vl">Next Visit</span>
                      <span className={`mc-vd ${isOverdue(m.nextVisitDate) ? 'due' : ''}`}>
                        {formatDate(m.nextVisitDate)} {isOverdue(m.nextVisitDate) ? '(Overdue)' : ''}
                      </span>
                    </div>
                  )}
                  {/* Actions Row */}
                  <div className="mc-actions">
                    {nextOptions.length > 0 && (
                      <select
                        className="mc-status-sel"
                        value=""
                        onChange={e => e.target.value && handleStatusChange(m.id, e.target.value)}
                        data-testid={`status-select-${m.id}`}
                      >
                        <option value="">Status: {m.status}</option>
                        {nextOptions.map(s => <option key={s} value={s}>→ {s}</option>)}
                      </select>
                    )}
                    <button
                      className="mc-act-btn"
                      onClick={() => openEditSheet(m)}
                      data-testid={`edit-btn-${m.id}`}
                    >
                      Edit
                    </button>
                    <button
                      className="mc-act-btn"
                      onClick={() => { setRescheduleCard(showReschedule ? null : m.id); setRescheduleDate(''); setRescheduleNote(''); }}
                      data-testid={`reschedule-btn-${m.id}`}
                    >
                      <CalendarClock size={12} /> Reschedule
                    </button>
                    <button
                      className="mc-act-btn"
                      onClick={() => setHistoryCard(showHistory ? null : m.id)}
                      data-testid={`history-btn-${m.id}`}
                    >
                      <History size={12} /> History
                    </button>
                  </div>
                  {/* Reschedule Inline */}
                  {showReschedule && (
                    <div className="mc-reschedule" data-testid={`reschedule-panel-${m.id}`}>
                      <div className="mc-reschedule-title">Reschedule Follow-up</div>
                      <textarea className="fi" rows={2} placeholder="Add notes about this visit..."
                        value={rescheduleNote} onChange={e => setRescheduleNote(e.target.value)}
                        style={{ marginBottom: '8px', fontSize: '12px' }} />
                      <div className="mc-reschedule-row">
                        <input type="date" className="fi" value={rescheduleDate}
                          onChange={e => setRescheduleDate(e.target.value)} />
                        <button className="btn-navy" onClick={() => handleReschedule(m.id)}>Save</button>
                      </div>
                    </div>
                  )}
                  {/* History Panel */}
                  {showHistory && (
                    <div className="mc-history">
                      <div className="mc-hist-title">Visit History ({m.visitLog?.length || 0})</div>
                      {(!m.visitLog || m.visitLog.length === 0) ? (
                        <div className="mc-hist-empty">No visits recorded yet</div>
                      ) : (
                        m.visitLog.slice(-3).reverse().map((v, i) => (
                          <div key={i} className="mc-hist-item">
                            <div className="mc-hist-date">{new Date(v.visitedAt || v.addedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</div>
                            <div className="mc-hist-note">{v.note}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="pager fu">
              <button className="pg-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <span className="pg-num">{page} / {pages}</span>
              <button className="pg-btn" disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </div>
        <div style={{ height: '20px' }} />
      </div>

      <AddMerchantSheet
        isOpen={showSheet}
        onClose={() => { setShowSheet(false); setEditingMerchant(null); }}
        onSuccess={editingMerchant ? handleEditSuccess : handleMerchantAdded}
        editMerchant={editingMerchant}
      />

      <Toast msg={toast.msg} show={toast.show} />
    </AgentLayout>
  );
}
